<template>
  <footer class="legal-footer" aria-label="Footer">
    <p class="legal-footer-copy">
      m&amp;s-Sprossenelemente GmbH - alle aufgef&uuml;hrten Preise zzgl. gesetzlicher MwSt. und Versand
      <template v-if="!showProtectedLinks && impressumLink">
        <span class="legal-footer-copy-separator">|</span>
        <a
          class="legal-footer-copy-link"
          :href="impressumLink.href"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ impressumLink.label }}
        </a>
      </template>
    </p>

    <nav v-if="showProtectedLinks" class="legal-footer-links" aria-label="Rechtliche Hinweise">
      <a
        v-for="link in legalLinks"
        :key="link.label"
        :href="link.href"
        target="_blank"
        rel="noopener noreferrer"
      >
        {{ link.label }}
      </a>
    </nav>

    <div v-if="showFeedbackButton" class="legal-footer-support">
      <button class="legal-footer-feedback-button" type="button" @click="openSupportTicketModal">
        <i class="fa-regular fa-message" aria-hidden="true"></i>
        <span>Feedback</span>
      </button>
    </div>

    <nav class="legal-footer-social" aria-label="Social Media">
      <a
        v-for="link in socialLinks"
        :key="link.label"
        :href="link.href"
        target="_blank"
        rel="noopener noreferrer"
        :aria-label="link.label"
      >
        <img :src="link.icon" :alt="link.label">
      </a>
    </nav>

    <a
      class="legal-footer-brand"
      href="https://www.sander-gruppe.eu/"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="m&s Webseite"
    >
      <img :src="sanderLogo" alt="Sander-Logo">
    </a>

    <div v-if="showFeedbackButton && showSupportTicketModal" class="support-ticket-modal-overlay" @click.self="closeSupportTicketModal">
      <form class="support-ticket-modal" role="dialog" aria-modal="true" aria-labelledby="support-ticket-modal-title" @submit.prevent="submitSupportTicket">
        <div class="support-ticket-modal-header">
          <span id="support-ticket-modal-title" class="support-ticket-modal-title">Feedback</span>
          <button type="button" class="support-ticket-modal-close" aria-label="Feedback schließen" :disabled="isSupportTicketSubmitting" @click="closeSupportTicketModal">×</button>
        </div>
        <div class="support-ticket-modal-content">
          <p class="support-ticket-intro">
            Über dieses Formular können Sie uns Feedback zum Konfigurator geben oder ein Problem melden.
          </p>
          <label class="support-ticket-field" for="support-ticket-subject">
            <span>Betreff</span>
            <input
              id="support-ticket-subject"
              ref="supportTicketSubjectInput"
              v-model="supportTicketForm.subject"
              type="text"
              maxlength="150"
              required
              :disabled="isSupportTicketSubmitting"
            />
          </label>
          <label class="support-ticket-field" for="support-ticket-message">
            <span>Text</span>
            <textarea
              id="support-ticket-message"
              v-model="supportTicketForm.message"
              maxlength="5000"
              required
              :disabled="isSupportTicketSubmitting"
            ></textarea>
          </label>
          <div class="support-ticket-field">
            <span>Screenshot</span>
            <input
              id="support-ticket-screenshot"
              ref="supportTicketScreenshotInput"
              class="support-ticket-file-input"
              type="file"
              accept="image/png,image/jpeg,image/gif"
              :disabled="isSupportTicketSubmitting"
              @change="handleSupportTicketScreenshotChange"
            />
            <label class="support-ticket-file-button" for="support-ticket-screenshot">
              <i class="fa fa-image" aria-hidden="true"></i>
              <span>{{ supportTicketScreenshotLabel }}</span>
            </label>
          </div>
          <div v-if="supportTicketError" class="support-ticket-message support-ticket-message-error">{{ supportTicketError }}</div>
          <div v-if="supportTicketSuccess" class="support-ticket-message support-ticket-message-success">{{ supportTicketSuccess }}</div>
        </div>
        <div class="support-ticket-modal-actions">
          <button type="button" :disabled="isSupportTicketSubmitting" @click="closeSupportTicketModal">Abbrechen</button>
          <button type="submit" :disabled="isSupportTicketSubmitting">
            {{ isSupportTicketSubmitting ? 'Wird gesendet...' : 'Absenden' }}
          </button>
        </div>
      </form>
    </div>
  </footer>
</template>

<script>
import api from '@/api/client';
import facebookIcon from '@/assets/footer/Icon-awesome-facebook.svg';
import instagramIcon from '@/assets/footer/Icon-awesome-instagram.svg';
import linkedinIcon from '@/assets/footer/Icon-awesome-linkedin.svg';
import xingIcon from '@/assets/footer/Icon-simple-xing.svg';
import youtubeIcon from '@/assets/footer/Icon-awesome-youtube.svg';
import whatsappIcon from '@/assets/footer/Icon-awesome-whatsapp-square.svg';
import sanderLogo from '@/assets/footer/Sander-Gruppe.png';

const LEGAL_PATHS = {
  privacy: '/legal/2026-05-20_Datenschutzerklaerung_Plattform_V1.0.pdf',
  marketing: '/legal/2026-05-20_Nutzungsvereinbarung_Marketingportal_V1.0.pdf',
  configurator: '/legal/2026-05-20_Nutzungsbedingungen_Konfigurator_V1.0.pdf',
};

const isAbsoluteUrl = (value) => /^(https?:|mailto:|tel:)/i.test(value);

const buildBackendUrl = (value) => {
  if (!value || isAbsoluteUrl(value)) {
    return value;
  }

  try {
    return new URL(value, api.defaults.baseURL || window.location.origin).toString();
  } catch {
    return value;
  }
};

export default {
  name: 'LegalFooter',
  props: {
    showProtectedLinks: {
      type: Boolean,
      default: true,
    },
    showFeedbackButton: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      sanderLogo,
      showSupportTicketModal: false,
      supportTicketForm: {
        subject: '',
        message: '',
        screenshot: null,
      },
      supportTicketError: '',
      supportTicketSuccess: '',
      isSupportTicketSubmitting: false,
      legalLinks: [
        {
          label: 'Impressum',
          href: process.env.VUE_APP_LEGAL_IMPRESSUM_URL || 'https://www.ms-sprossenelemente.de/impressum/',
          public: true,
        },
        {
          label: 'Datenschutz',
          href: buildBackendUrl(process.env.VUE_APP_LEGAL_PRIVACY_PDF_URL || LEGAL_PATHS.privacy),
        },
        {
          label: 'Nutzungsvereinbarung Marketingportal',
          href: buildBackendUrl(process.env.VUE_APP_LEGAL_MARKETING_TERMS_PDF_URL || LEGAL_PATHS.marketing),
        },
        {
          label: 'Nutzungsvereinbarungen Konfigurator',
          href: buildBackendUrl(process.env.VUE_APP_LEGAL_CONFIGURATOR_TERMS_PDF_URL || LEGAL_PATHS.configurator),
        },
      ],
      socialLinks: [
        {
          label: 'Facebook',
          href: 'https://www.facebook.com/msInsektenschutz',
          icon: facebookIcon,
        },
        {
          label: 'Instagram',
          href: 'https://www.instagram.com/ms_insektenschutz/',
          icon: instagramIcon,
        },
        {
          label: 'LinkedIn',
          href: 'https://de.linkedin.com/company/m-s-sprossenelemente-gmbh',
          icon: linkedinIcon,
        },
        {
          label: 'Xing',
          href: 'https://www.xing.com/pages/m-ssprossenelementegmbh-insektenschutzgitteru-vorsatzsprossen',
          icon: xingIcon,
        },
        {
          label: 'Youtube',
          href: 'https://www.youtube.com/@mssprossenelementegmbh9629/videos',
          icon: youtubeIcon,
        },
        {
          label: 'Whatsapp',
          href: 'https://wa.me/message/BWHQ35ZD2AQYJ1',
          icon: whatsappIcon,
        },
      ],
    };
  },
  computed: {
    impressumLink() {
      return this.legalLinks.find((link) => link.public);
    },
    supportTicketScreenshotLabel() {
      return this.supportTicketForm.screenshot?.name || 'Bild auswählen';
    },
  },
  mounted() {
    document.addEventListener('keydown', this.handleDocumentKeyDown);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleDocumentKeyDown);
  },
  methods: {
    handleDocumentKeyDown(event) {
      if (event.key === 'Escape' && this.showSupportTicketModal) {
        this.closeSupportTicketModal();
      }
    },
    openSupportTicketModal() {
      this.resetSupportTicketForm();
      this.showSupportTicketModal = true;
      this.$nextTick(() => {
        this.$refs.supportTicketSubjectInput?.focus();
      });
    },
    closeSupportTicketModal() {
      if (this.isSupportTicketSubmitting) {
        return;
      }

      this.showSupportTicketModal = false;
      this.resetSupportTicketForm();
    },
    resetSupportTicketForm() {
      this.supportTicketForm = {
        subject: '',
        message: '',
        screenshot: null,
      };
      this.supportTicketError = '';
      this.supportTicketSuccess = '';
      if (this.$refs.supportTicketScreenshotInput) {
        this.$refs.supportTicketScreenshotInput.value = '';
      }
    },
    handleSupportTicketScreenshotChange(event) {
      const file = event.target.files?.[0] || null;
      this.supportTicketError = '';
      this.supportTicketSuccess = '';

      if (!file) {
        this.supportTicketForm.screenshot = null;
        return;
      }

      const isSupportedImage = ['image/png', 'image/jpeg', 'image/gif'].includes(file.type);
      if (!isSupportedImage) {
        this.supportTicketForm.screenshot = null;
        event.target.value = '';
        this.supportTicketError = 'Bitte laden Sie ein Bild im PNG-, JPG- oder GIF-Format hoch.';
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        this.supportTicketForm.screenshot = null;
        event.target.value = '';
        this.supportTicketError = 'Der Screenshot darf maximal 10 MB groß sein.';
        return;
      }

      this.supportTicketForm.screenshot = file;
    },
    async submitSupportTicket() {
      if (this.isSupportTicketSubmitting) {
        return;
      }

      const subject = this.supportTicketForm.subject.trim();
      const message = this.supportTicketForm.message.trim();

      if (!subject || !message) {
        this.supportTicketError = 'Bitte füllen Sie Betreff und Text aus.';
        return;
      }

      const formData = new FormData();
      formData.append('subject', subject);
      formData.append('message', message);
      if (this.supportTicketForm.screenshot) {
        formData.append('screenshot', this.supportTicketForm.screenshot);
      }

      this.isSupportTicketSubmitting = true;
      this.supportTicketError = '';
      this.supportTicketSuccess = '';

      try {
        const response = await api.post('/api/support-ticket', formData);
        this.supportTicketSuccess = response.data?.message || 'Ihr Ticket wurde erfolgreich gesendet.';
        window.setTimeout(() => {
          if (this.showSupportTicketModal && this.supportTicketSuccess) {
            this.closeSupportTicketModal();
          }
        }, 1400);
      } catch (error) {
        console.error('Fehler beim Senden des Support-Tickets:', error);
        this.supportTicketError = 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.';
      } finally {
        this.isSupportTicketSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.legal-footer {
  width: min(100%, 900px);
  margin: 80px auto 0;
  padding: 0 16px 28px;
  color: #747474;
  text-align: center;
  box-sizing: border-box;
}

.legal-footer-copy {
  margin: 0 0 20px;
  font-size: 10pt;
  line-height: 1.4;
}

.legal-footer-copy-separator {
  display: inline-block;
  margin: 0 8px;
  color: #b8b8b8;
}

.legal-footer-copy-link {
  color: #5f5f5f;
  font-weight: 600;
  text-decoration: none;
}

.legal-footer-copy-link:hover,
.legal-footer-copy-link:focus-visible {
  color: #e30613;
  text-decoration: underline;
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;
}

.legal-footer-links {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  align-items: center;
  justify-content: center;
  width: fit-content;
  max-width: 100%;
  margin: 0 auto 20px;
  padding: 10px 16px;
  border-top: 1px solid #e2e2e2;
  border-bottom: 1px solid #e2e2e2;
}

.legal-footer-links a {
  color: #5f5f5f;
  font-size: 0.86rem;
  font-weight: 600;
  line-height: 1.35;
  text-decoration: none;
  transition: color 0.15s ease, text-decoration-color 0.15s ease;
}

.legal-footer-links a:hover,
.legal-footer-links a:focus-visible {
  color: #e30613;
  text-decoration: underline;
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;
}

.legal-footer-support {
  display: flex;
  justify-content: center;
  margin: -4px 0 20px;
}

.legal-footer-feedback-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 38px;
  padding: 8px 14px;
  border: 1px solid #d5d5d5;
  border-radius: 6px;
  background: #ffffff;
  color: #5f5f5f;
  box-shadow: 0 2px 7px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  font: inherit;
  font-size: 0.88rem;
  font-weight: 700;
  line-height: 1;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, color 0.15s ease, transform 0.15s ease;
}

.legal-footer-feedback-button i {
  color: #e30613;
  font-size: 0.95rem;
}

.legal-footer-feedback-button:hover,
.legal-footer-feedback-button:focus-visible {
  border-color: #e30613;
  color: #333333;
  box-shadow: 0 5px 14px rgba(227, 6, 19, 0.14);
  outline: none;
  transform: translateY(-1px);
}

.support-ticket-modal-overlay {
  position: fixed;
  inset: 0;
  padding: 24px;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.support-ticket-modal {
  width: min(920px, 100%);
  height: min(720px, calc(100vh - 48px));
  background: #ffffff;
  border: 1px solid #747474;
  border-radius: 8px;
  box-shadow: 0 18px 36px rgba(0, 0, 0, 0.22);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: #4d4d4d;
}

.support-ticket-modal-header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 52px;
  padding: 0.75rem 1rem;
  background-color: #c90510;
  color: #ffffff;
  font-size: 16pt;
  font-weight: lighter;
}

.support-ticket-modal-title {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  font-weight: 600;
  line-height: 1.25;
}

.support-ticket-modal-close {
  width: 34px;
  height: 34px;
  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: 5px;
  background: transparent;
  color: #ffffff;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
}

.support-ticket-modal-close:not(:disabled):hover {
  background-color: rgba(255, 255, 255, 0.12);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
}

.support-ticket-modal-content {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 34px 44px 18px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  text-align: left;
}

.support-ticket-intro {
  margin: 0;
  color: #666666;
  font-size: 11pt;
  line-height: 1.5;
}

.support-ticket-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  color: #4d4d4d;
  font-size: 10.5pt;
  font-weight: 700;
}

.support-ticket-field input[type="text"],
.support-ticket-field textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #747474;
  border-radius: 5px;
  background-color: #ffffff;
  color: #4d4d4d;
  font: inherit;
  font-weight: 400;
  line-height: 1.35;
  padding: 0.65rem 0.75rem;
}

.support-ticket-field textarea {
  min-height: 240px;
  resize: vertical;
}

.support-ticket-field input[type="text"]:focus,
.support-ticket-field textarea:focus {
  outline: none;
  border-color: #e30613;
  box-shadow: 0 0 5px rgba(227, 6, 19, 0.4);
}

.support-ticket-file-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.support-ticket-file-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  width: fit-content;
  max-width: 100%;
  min-height: 40px;
  box-sizing: border-box;
  padding: 0.55rem 0.8rem;
  border: 1px solid #747474;
  border-radius: 5px;
  background-color: transparent;
  color: #747474;
  cursor: pointer;
  font-weight: 400;
  transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.support-ticket-file-button span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.support-ticket-file-button:hover {
  background-color: #f6f6f6;
  color: #333333;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.support-ticket-message {
  padding: 0.65rem 0.75rem;
  border-radius: 5px;
  font-size: 10pt;
  line-height: 1.4;
}

.support-ticket-message-error {
  border: 1px solid #e30613;
  background-color: #fff5f5;
  color: #a9040d;
}

.support-ticket-message-success {
  border: 1px solid #3c9d5d;
  background-color: #f3fbf6;
  color: #24633c;
}

.support-ticket-modal-actions {
  flex: 0 0 auto;
  display: flex;
  justify-content: flex-end;
  gap: 0.65rem;
  padding: 0 44px 32px;
}

.support-ticket-modal-actions button {
  min-width: 112px;
  padding: 0.65rem 0.85rem;
  border: 1px solid #747474;
  border-radius: 5px;
  background-color: transparent;
  color: #747474;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.support-ticket-modal-actions button[type="submit"] {
  border-color: #c90510;
  background-color: #c90510;
  color: #ffffff;
}

.support-ticket-modal-actions button:not(:disabled):hover {
  background-color: #f6f6f6;
  color: #333333;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.support-ticket-modal-actions button[type="submit"]:not(:disabled):hover {
  border-color: #a9040d;
  background-color: #a9040d;
  color: #ffffff;
}

.support-ticket-modal-actions button:disabled,
.support-ticket-modal-close:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.legal-footer-social {
  display: flex;
  gap: 11px;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.legal-footer-social a,
.legal-footer-brand {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.legal-footer-social img {
  width: 1.5rem;
  max-width: 1.5rem;
  height: auto;
  transition: transform 0.15s ease, opacity 0.15s ease;
}

.legal-footer-social a:hover img,
.legal-footer-social a:focus-visible img,
.legal-footer-brand:hover img,
.legal-footer-brand:focus-visible img {
  opacity: 0.8;
  transform: translateY(-1px);
}

.legal-footer-brand img {
  width: 3.6rem;
  max-width: 3.6rem;
  height: auto;
}

@media (max-width: 640px) {
  .legal-footer {
    margin-top: 48px;
    padding-inline: 12px;
  }

  .legal-footer-links {
    gap: 7px 12px;
    padding-inline: 10px;
  }

  .legal-footer-links a {
    font-size: 0.8rem;
  }

  .legal-footer-support {
    margin-bottom: 18px;
  }

  .legal-footer-feedback-button {
    width: min(100%, 220px);
  }

  .support-ticket-modal-overlay {
    padding: 12px;
  }

  .support-ticket-modal {
    height: calc(100vh - 24px);
  }

  .support-ticket-modal-header {
    min-height: 48px;
    font-size: 14pt;
  }

  .support-ticket-modal-content {
    padding: 20px 16px 16px;
  }

  .support-ticket-field textarea {
    min-height: 180px;
  }

  .support-ticket-modal-actions {
    flex-direction: column-reverse;
    padding: 0 16px 20px;
  }

  .support-ticket-modal-actions button {
    width: 100%;
  }
}
</style>
